import { ColumnInformationDto } from "components/annotations/interface";
import { AitCellData } from "./AitCellData";
import { AitRowData } from "./AitRowData";
import { AitRowGroupData } from "./AitRowGroupData";
import {
  AitHeaderCellData,
  AitHeaderGroupData,
  AitHeaderRowData,
  AitLocation,
  AitLocationBody,
  AitLocationBodyCell,
  AitLocationBodyRow,
  AitLocationBodyRowGroup,
  AitLocationColumn,
  AitLocationHeader,
  AitLocationHeaderCell,
  AitLocationHeaderRow,
  AitLocationTable,
  AitRowType,
  AitTableData,
  AitTablePosition,
  AitTableState,
} from "./interface";

import { trGetCellFromLocation } from "./trGetCellFromLocation";

export interface tablePositionReturn<T> {
  tableLevel: AitTablePosition.table;
  data?: AitTableData<T>;
}
export interface headerPositionReturn<T> {
  tableLevel: AitTablePosition.header;
  data?: AitHeaderGroupData<T>;
}
export interface headerRowPositionReturn<T> {
  tableLevel: AitTablePosition.headerRow;
  data?: AitHeaderRowData<T>;
}
export interface headerCellPositionReturn<T> {
  tableLevel: AitTablePosition.headerCell;
  data?: AitHeaderCellData<T>;
}
export interface bodyPositionReturn<T> {
  tableLevel: AitTablePosition.body;
  data?: AitRowGroupData<T>[];
}
export interface bodyRowGroupPositionReturn<T> {
  tableLevel: AitTablePosition.bodyRowGroup;
  data?: AitRowGroupData<T>;
}
export interface bodyRowPositionReturn<T> {
  tableLevel: AitTablePosition.bodyRow;
  data?: AitRowData<T>;
}
export interface bodyCellPositionReturn<T> {
  tableLevel: AitTablePosition.bodyCell;
  data?: AitCellData<T>;
}
export interface columnPositionReturn {
  tableLevel: AitTablePosition.column;
  data?: ColumnInformationDto;
}

type positionReturn<T> =
  | tablePositionReturn<T>
  | headerPositionReturn<T>
  | headerRowPositionReturn<T>
  | headerCellPositionReturn<T>
  | bodyPositionReturn<T>
  | bodyRowGroupPositionReturn<T>
  | bodyRowPositionReturn<T>
  | bodyCellPositionReturn<T>
  | columnPositionReturn
  | { data: undefined; tableLevel: undefined };

/**
 * Resolve the data object at a given table `location`.
 *
 * The function accepts a table state (original or processed) and a location descriptor
 * and returns a discriminated union describing the table level found and the associated
 * data for that level. Overloads provide improved typing for callers depending on the
 * specific `AitLocation` subtype passed in.
 *
 * @param state - Table state object (may be `null`) containing original and processed table data
 * @param location - Location descriptor indicating where to look in the table (header/body/column/row/cell)
 * @param processed - When true, the processed table data is used; otherwise the original table data
 * @returns A discriminated union containing `tableLevel` (an `AitTablePosition` value) and,
 *          when present, a `data` property holding the object at that location (for example
 *          `AitTableData`, `AitHeaderGroupData`, `AitHeaderRowData`, `AitRowData`, `AitCellData`,
 *          or `ColumnInformationDto`). When the requested location cannot be resolved the
 *          function returns an object with both properties undefined: `{ data: undefined, tableLevel: undefined }`.
 */
// Function overloads
export function trGetPositionFromLocation<T>(
  state: AitTableState<T> | null,
  location: AitLocationTable,
  processed?: boolean,
): tablePositionReturn<T>;

export function trGetPositionFromLocation<T>(
  state: AitTableState<T> | null,
  location: AitLocationHeader,
  processed?: boolean,
): headerPositionReturn<T>;

export function trGetPositionFromLocation<T>(
  state: AitTableState<T> | null,
  location: AitLocationHeaderRow,
  processed?: boolean,
): headerRowPositionReturn<T>;

export function trGetPositionFromLocation<T>(
  state: AitTableState<T> | null,
  location: AitLocationHeaderCell,
  processed?: boolean,
): headerCellPositionReturn<T>;

export function trGetPositionFromLocation<T>(
  state: AitTableState<T> | null,
  location: AitLocationBody,
  processed?: boolean,
): bodyPositionReturn<T>;

export function trGetPositionFromLocation<T>(
  state: AitTableState<T> | null,
  location: AitLocationBodyRowGroup,
  processed?: boolean,
): bodyRowGroupPositionReturn<T>;

export function trGetPositionFromLocation<T>(
  state: AitTableState<T> | null,
  location: AitLocationHeader | AitLocationBodyRowGroup,
  processed?: boolean,
): headerPositionReturn<T> | bodyRowGroupPositionReturn<T>;

export function trGetPositionFromLocation<T>(
  state: AitTableState<T> | null,
  location: AitLocationBodyRow,
  processed?: boolean,
): bodyRowPositionReturn<T>;

export function trGetPositionFromLocation<T>(
  state: AitTableState<T> | null,
  location: AitLocationBodyCell,
  processed?: boolean,
): bodyCellPositionReturn<T>;

export function trGetPositionFromLocation<T>(
  state: AitTableState<T> | null,
  location: AitLocationColumn,
  processed?: boolean,
): columnPositionReturn;

export function trGetPositionFromLocation<T>(
  state: AitTableState<T> | null,
  location?: AitLocation,
  processed?: boolean,
): positionReturn<T>;

// General function implementation
export function trGetPositionFromLocation<T>(
  state: AitTableState<T> | null,
  location?: AitLocation,
  processed: boolean = false,
): positionReturn<T> {
  /* Check state */
  if (!state) {
    return { data: undefined, tableLevel: undefined };
  }

  const tableData = processed ? state.processedTableData : state.originalTableData;

  /* Table level */
  if (!location || location.position === AitTablePosition.table) {
    return {
      tableLevel: AitTablePosition.table,
      data: tableData,
    };
  } else if (location.position === AitTablePosition.column) {
    /* Column level - find the column information for this column */
    const columnInfo = tableData?.columnInformation.find((ci) => ci.columnId === location.columnId);
    if (columnInfo) {
      return {
        tableLevel: AitTablePosition.column,
        data: columnInfo,
      };
    } else {
      return { data: undefined, tableLevel: undefined };
    }
  } else if (location.tableSection === AitRowType.header && tableData?.headerData) {
    /* Header data */
    if (location.position === AitTablePosition.headerCell) {
      return {
        tableLevel: location.position,
        data: trGetCellFromLocation(tableData, location),
      };
    } else if (location.position === AitTablePosition.headerRow) {
      return {
        tableLevel: location.position,
        data:
          tableData.headerData.rows[location.row].aitid === location.rowId
            ? tableData.headerData.rows[location.row]
            : undefined,
      };
    } else {
      return { tableLevel: location.position, data: tableData?.headerData };
    }
  } else if (location.tableSection === AitRowType.body && tableData?.bodyData) {
    /* Body data */
    /* istanbul ignore else */
    if (location.position === AitTablePosition.bodyCell) {
      return {
        tableLevel: location.position,
        data: trGetCellFromLocation(tableData, location),
      };
    } else if (location.position === AitTablePosition.bodyRow) {
      return {
        tableLevel: location.position,
        data:
          tableData.bodyData[location.rowGroup].rows[location.row].aitid === location.rowId
            ? tableData.bodyData[location.rowGroup].rows[location.row]
            : undefined,
      };
    } else if (location.position === AitTablePosition.bodyRowGroup) {
      return {
        tableLevel: location.position,
        data: tableData.bodyData[location.rowGroup],
      };
    } else if (location.position === AitTablePosition.body) {
      return { tableLevel: location.position, data: tableData.bodyData };
    }
  }
  return { data: undefined, tableLevel: undefined };
}
